import { useEffect, useRef, useState } from 'react';
import { Tag, X, ChevronDown, Plus } from 'lucide-react';
import { crmApi, Etiqueta } from './crmApi';

interface Props {
  session: any;
  value: string[];          // nomes das etiquetas selecionadas
  onChange: (v: string[]) => void;
}

const CORES_RAPIDAS = ['#6b7280', '#3b82f6', '#22c55e', '#eab308', '#f97316', '#ef4444', '#a855f7', '#ec4899', '#14b8a6'];

const EtiquetasPicker = ({ session, value, onChange }: Props) => {
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  const [corNova, setCorNova] = useState(CORES_RAPIDAS[2]); // verde por padrão
  const ref = useRef<HTMLDivElement>(null);

  const recarregar = () => crmApi.listarEtiquetas(session).then(setEtiquetas).catch(() => {});

  useEffect(() => { recarregar(); }, []); // eslint-disable-line

  // fecha ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false); setCriando(false); setBusca('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (nome: string) => {
    if (value.includes(nome)) onChange(value.filter(v => v !== nome));
    else { onChange([...value, nome]); setBusca(''); }
  };

  const colorOf = (nome: string) => etiquetas.find(e => e.nome === nome)?.cor || '#6b7280';

  const nomeBuscaTrimmed = busca.trim();
  const disponiveis = etiquetas.filter(
    e => !value.includes(e.nome) && e.nome.toLowerCase().includes(busca.toLowerCase())
  );
  const podecriar = nomeBuscaTrimmed.length >= 1 &&
    !etiquetas.some(e => e.nome.toLowerCase() === nomeBuscaTrimmed.toLowerCase());

  const criarInline = async () => {
    if (!nomeBuscaTrimmed) return;
    setCriando(true);
    try {
      const nova = await crmApi.criarEtiqueta(session, nomeBuscaTrimmed, corNova);
      await recarregar();
      onChange([...value, nova.nome]);
      setBusca('');
      setCorNova(CORES_RAPIDAS[2]);
    } catch (e: any) { alert('Erro ao criar etiqueta: ' + e.message); }
    finally { setCriando(false); }
  };

  return (
    <div ref={ref} className="space-y-1.5">
      <label className="text-xs text-gray-600 flex items-center gap-1">
        <Tag className="h-3 w-3" /> Etiquetas
      </label>

      {/* Pills selecionadas */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map(nome => {
            const cor = colorOf(nome);
            return (
              <span key={nome}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-white"
                style={{ backgroundColor: cor }}>
                {nome}
                <button type="button" onClick={() => toggle(nome)}
                  className="hover:opacity-70 transition-opacity">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Botão para abrir dropdown */}
      <div className="relative">
        <button type="button" onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors">
          <span>Adicionar etiqueta</span>
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            {/* Busca */}
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                value={busca}
                onChange={e => setBusca(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && podecriar) criarInline(); }}
                placeholder="Buscar ou criar etiqueta…"
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300" />
            </div>

            {/* Lista de existentes */}
            <div className="max-h-36 overflow-y-auto">
              {disponiveis.length === 0 && !podecriar && (
                <p className="text-[11px] text-gray-400 text-center py-3">
                  {etiquetas.length === 0 ? 'Nenhuma etiqueta cadastrada' : 'Todas já selecionadas'}
                </p>
              )}
              {disponiveis.map(e => (
                <button key={e.id} type="button" onClick={() => toggle(e.nome)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left transition-colors">
                  <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: e.cor }} />
                  <span className="text-xs text-gray-800">{e.nome}</span>
                </button>
              ))}
            </div>

            {/* Criar nova etiqueta inline */}
            {podecriar && (
              <div className="border-t border-gray-100 p-2 space-y-1.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Criar nova</p>
                <div className="flex items-center gap-1.5">
                  {/* Seletor de cor rápido */}
                  <div className="flex gap-0.5">
                    {CORES_RAPIDAS.map(c => (
                      <button key={c} type="button" onClick={() => setCorNova(c)}
                        className={`h-4 w-4 rounded-full border-2 transition-all ${corNova === c ? 'border-gray-700 scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                <button type="button" onClick={criarInline} disabled={criando}
                  className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: corNova }}>
                  <Plus className="h-3 w-3" />
                  {criando ? 'Criando…' : `Criar "${nomeBuscaTrimmed}"`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EtiquetasPicker;

